import { useRef } from 'react';
import Modal from '../../../components/Modal';
import { AssetRef, Shot } from '../../../types';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface DuplicateShotModalProps {
  target: Shot | null;
  loading: boolean;
  t: Translate;
  onClose: () => void;
  onConfirm: () => void;
}

const PROMPT_PREVIEW_MAX_CHARS = 200;

function parseAssetRefCount(assetRefs?: string): number {
  if (!assetRefs) return 0;
  try {
    const refs: AssetRef[] = JSON.parse(assetRefs);
    return Array.isArray(refs) ? refs.length : 0;
  } catch {
    return 0;
  }
}

export default function DuplicateShotModal({ target, loading, t, onClose, onConfirm }: DuplicateShotModalProps) {
  // 缓存最后一个非空 target：target 置 null 时让 Modal 播放退出动画（open=false），
  // 期间仍用缓存内容渲染，避免内容闪空。与 InsertShotModal 的常驻挂载行为对齐。
  const lastTargetRef = useRef<Shot | null>(null);
  if (target) lastTargetRef.current = target;
  const shown = target ?? lastTargetRef.current;
  if (!shown) return null;

  const assetCount = parseAssetRefCount(shown.assetRefs);
  const resourceCount = shown.resources?.length ?? 0;
  const promptPreview = shown.prompt.length > PROMPT_PREVIEW_MAX_CHARS
    ? `${shown.prompt.slice(0, PROMPT_PREVIEW_MAX_CHARS)}…`
    : shown.prompt;

  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title={t('duplicateModal.title', { index: shown.index })}
      width="480px"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>{t('cancel', { ns: 'common' })}</button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={loading}>
            {loading ? <><span className="loading-spinner"></span>{t('duplicateModal.duplicating')}</> : t('duplicateModal.confirm')}
          </button>
        </>
      }
    >
      <p style={{ color: 'var(--text-2)', fontSize: '0.9rem', marginTop: 0 }}>{t('duplicateModal.hint')}</p>
      <div className="form-group">
        <label className="form-label">{t('duplicateModal.promptLabel')}</label>
        <div style={{ whiteSpace: 'pre-wrap', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '0.5rem 0.75rem', fontSize: '0.88rem', maxHeight: '160px', overflow: 'auto' }}>
          {promptPreview}
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">{t('duplicateModal.refLabel')}</label>
        <div style={{ fontSize: '0.88rem' }}>{t('duplicateModal.refValue', { assetCount, resourceCount })}</div>
      </div>
      <div className="form-group">
        <label className="form-label">{t('duplicateModal.durationLabel')}</label>
        <div style={{ fontSize: '0.88rem' }}>{t('duplicateModal.durationValue', { duration: shown.duration })}</div>
      </div>
    </Modal>
  );
}
